(() => {
  'use strict';

  const w = window;
  const d = document;
  d.documentElement.dataset.kraStage = 'loaded';
  const intelligenceNode = d.getElementById('KloudzIntelligenceConfig');
  const contextNode = d.getElementById('KloudzRelationshipContext');
  const panel = d.querySelector('[data-kra-panel]');
  const launcher = d.querySelector('[data-kra-launch]');
  if (!intelligenceNode || !contextNode || !panel || !launcher) return;
  if (launcher.dataset.kraBound === 'true') {
    d.documentElement.dataset.kraStage = 'ready';
    return;
  }
  launcher.dataset.kraBound = 'true';

  let config;
  let pageContext;
  try {
    config = JSON.parse(intelligenceNode.textContent || '{}');
    pageContext = JSON.parse(contextNode.textContent || '{}');
  } catch (_) { return; }
  d.documentElement.dataset.kraStage = 'context-ready';
  const endpoint = String(config.endpoint || '').replace(/\/+$/, '');
  if (!config.enabled || !/^https:\/\//i.test(endpoint)) return;

  const $ = (selector, root = d) => root.querySelector(selector);
  const $$ = (selector, root = d) => [...root.querySelectorAll(selector)];
  const conversation = $('[data-kra-conversation]', panel);
  const form = $('[data-kra-form]', panel);
  const input = $('[data-kra-input]', panel);
  const sendButton = $('[data-kra-send]', panel);
  const status = $('[data-kra-status]', panel);
  const unread = $('[data-kra-unread]', launcher);
  const memoryPanel = $('[data-kra-memory]', panel);
  const memoryForm = $('[data-kra-memory-form]', panel);
  const imageInput = $('[data-kra-image]', panel);
  const attachment = $('[data-kra-attachment]', panel);
  const attachmentPreview = $('[data-kra-attachment-preview]', panel);
  const attachmentName = $('[data-kra-attachment-name]', panel);
  d.documentElement.dataset.kraStage = 'dom-ready';
  const TOKEN_KEY = 'kloudz_relationship_session_1';
  const VERSION = '1.0.0';
  const transientHistory = [];
  let selectedImage = null;
  let busy = false;
  let lastLauncherPointer = 0;
  let privacy = { analytics: false, memory: false };
  let previousMemoryConsent = false;

  const safeStore = (storage) => {
    try {
      const key = '__kra_test__';
      storage.setItem(key, '1');
      storage.removeItem(key);
      return storage;
    } catch (_) { return null; }
  };
  const sessionStore = safeStore(w.sessionStorage);
  const localStore = safeStore(w.localStorage);
  const clean = (value, length = 120) => String(value == null ? '' : value).replace(/[<>\u0000-\u001f]/g, '').trim().slice(0, length);
  const uid = () => {
    try { return w.crypto.randomUUID(); } catch (_) { return `kra-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 9)}`; }
  };
  const privacyAPI = () => w.Shopify && w.Shopify.customerPrivacy;
  const privacyCall = (name) => {
    const api = privacyAPI();
    try { return Boolean(api && typeof api[name] === 'function' && api[name]()); } catch (_) { return false; }
  };
  const readToken = () => {
    try {
      return (privacy.memory && localStore && localStore.getItem(TOKEN_KEY)) || (sessionStore && sessionStore.getItem(TOKEN_KEY)) || '';
    } catch (_) { return ''; }
  };
  const storeToken = (token) => {
    try {
      if (sessionStore) sessionStore.setItem(TOKEN_KEY, token);
      if (privacy.memory && localStore) localStore.setItem(TOKEN_KEY, token);
    } catch (_) {}
  };
  const removeToken = () => {
    try {
      if (sessionStore) sessionStore.removeItem(TOKEN_KEY);
      if (localStore) localStore.removeItem(TOKEN_KEY);
    } catch (_) {}
  };

  async function callAPI(path, payload, timeout = 32000) {
    const controller = new AbortController();
    const timer = w.setTimeout(() => controller.abort(), timeout);
    try {
      const response = await fetch(`${endpoint}${path}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Kloudz-Protocol': 'relationship/1',
          'X-Request-ID': uid()
        },
        body: JSON.stringify(payload),
        signal: controller.signal,
        credentials: 'omit',
        cache: 'no-store'
      });
      let body = {};
      try { body = await response.json(); } catch (_) {}
      if (!response.ok) {
        const error = new Error(clean(body.error, 80) || `request_${response.status}`);
        error.status = response.status;
        throw error;
      }
      return body;
    } finally { w.clearTimeout(timer); }
  }

  async function ensureSession(force = false) {
    if (force) removeToken();
    const existing = readToken();
    if (existing) return existing;
    const response = await callAPI('/v1/assistant/session', { protocol: 'kloudz-relationship-session/1' }, 10000);
    const token = String(response.sessionToken || '');
    if (!token) throw new Error('session_unavailable');
    storeToken(token);
    return token;
  }

  async function refreshPrivacy() {
    previousMemoryConsent = privacy.memory;
    privacy = {
      analytics: Boolean(config.telemetry && privacyCall('analyticsProcessingAllowed')),
      memory: privacyCall('preferencesProcessingAllowed')
    };
    if (privacy.memory) {
      const token = readToken();
      if (token && localStore) localStore.setItem(TOKEN_KEY, token);
    } else if (previousMemoryConsent) {
      const token = readToken();
      try {
        if (token) {
          await callAPI('/v1/assistant/memory', {
            protocol: 'kloudz-relationship-memory/1', sessionToken: token,
            action: 'clear', consent: { memory: false, analytics: privacy.analytics }
          }, 10000);
        }
      } catch (_) {}
      removeToken();
    }
    const notice = $('[data-kra-memory-consent]', memoryPanel);
    if (notice) notice.textContent = privacy.memory
      ? 'Memory is on because preference processing is allowed. You remain in control.'
      : 'Memory is off. Enable preference consent in cookie settings before saving personal preferences.';
    $$('input, [data-kra-memory-save]', memoryForm).forEach((node) => { node.disabled = !privacy.memory; });
  }

  function emit(name, payload = {}) {
    if (!privacy.analytics || !w.KloudzIntelligence || typeof w.KloudzIntelligence.event !== 'function') return;
    try { w.KloudzIntelligence.event(name, { assistant: VERSION, ...payload }); } catch (_) {}
  }

  function appendMessage(role, text, options = {}) {
    const article = d.createElement('article');
    article.className = `kra-message kra-message--${role}`;
    const body = d.createElement('div');
    body.className = 'kra-message__body';
    body.textContent = text;
    article.appendChild(body);
    if (options.meta) {
      const meta = d.createElement('div');
      meta.className = 'kra-message__meta';
      meta.textContent = options.meta;
      article.appendChild(meta);
    }
    if (options.why) {
      const why = d.createElement('details');
      why.className = 'kra-message__why';
      const summary = d.createElement('summary');
      summary.textContent = 'Why this answer';
      const detail = d.createElement('span');
      detail.textContent = options.why;
      why.append(summary, detail);
      article.appendChild(why);
    }
    conversation.appendChild(article);
    conversation.scrollTop = conversation.scrollHeight;
    return article;
  }

  function appendTyping() {
    const article = d.createElement('article');
    article.className = 'kra-message kra-message--assistant kra-message--typing';
    const body = d.createElement('div');
    body.className = 'kra-message__body';
    body.setAttribute('aria-label', 'Kloudz is thinking');
    body.append(d.createElement('i'), d.createElement('i'), d.createElement('i'));
    article.appendChild(body);
    conversation.appendChild(article);
    conversation.scrollTop = conversation.scrollHeight;
    return article;
  }

  function currentWelcome() {
    if (pageContext.product && pageContext.product.title) {
      return `I’m here for ${clean(pageContext.product.title, 80)}. Ask me to compare it, check fit or compatibility, or decide the best next step.`;
    }
    if (pageContext.pageType === 'cart') return 'Want a second set of eyes on your cart? I can help check fit, compatibility, and what you may not need.';
    if (String(pageContext.pageType).includes('account')) return 'I can help you understand an order or choose the safest next step without asking for credentials.';
    return 'Hi — I’m Kloudz. I can help you choose, compare, troubleshoot, understand policies, or find the clearest next step.';
  }

  function openPanel() {
    panel.hidden = false;
    panel.setAttribute('aria-hidden', 'false');
    launcher.setAttribute('aria-expanded', 'true');
    unread.hidden = true;
    const welcome = $('[data-kra-welcome] .kra-message__body', panel);
    if (welcome) welcome.textContent = currentWelcome();
    w.setTimeout(() => input.focus(), 80);
    emit('relationship_assistant_open', { pageType: pageContext.pageType });
  }

  function closePanel() {
    panel.hidden = true;
    panel.setAttribute('aria-hidden', 'true');
    launcher.setAttribute('aria-expanded', 'false');
    launcher.focus();
  }

  // The legacy Halo theme can replace floating controls after scripts bind.
  // Capture activation at the document boundary and resolve the live nodes so
  // the assistant remains available even after a section refresh.
  let delegatedPointerAt = 0;
  function setLivePanel(open, liveLauncher, livePanel) {
    livePanel.hidden = !open;
    livePanel.setAttribute('aria-hidden', String(!open));
    liveLauncher.setAttribute('aria-expanded', String(open));
    const liveUnread = $('[data-kra-unread]', liveLauncher);
    if (liveUnread && open) liveUnread.hidden = true;
    if (open) {
      const welcome = $('[data-kra-welcome] .kra-message__body', livePanel);
      if (welcome) welcome.textContent = currentWelcome();
      const liveInput = $('[data-kra-input]', livePanel);
      w.setTimeout(() => liveInput?.focus(), 80);
      emit('relationship_assistant_open', { pageType: pageContext.pageType });
    } else liveLauncher.focus();
  }
  function liveLauncherAction(event, pointer = false) {
    const liveLauncher = event.target.closest?.('[data-kra-launch]');
    const liveClose = event.target.closest?.('[data-kra-close]');
    if (!liveLauncher && !liveClose) return;
    const resolvedLauncher = liveLauncher || $('[data-kra-launch]');
    const livePanel = $('[data-kra-panel]');
    if (!resolvedLauncher || !livePanel) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (liveClose) {
      setLivePanel(false, resolvedLauncher, livePanel);
      return;
    }
    if (pointer) delegatedPointerAt = Date.now();
    else if (Date.now() - delegatedPointerAt < 700) return;
    setLivePanel(livePanel.hidden, resolvedLauncher, livePanel);
  }

  d.addEventListener('pointerdown', (event) => {
    if (event.button === 0) liveLauncherAction(event, true);
  }, true);
  d.addEventListener('click', (event) => liveLauncherAction(event), true);

  function setBusy(value, message = '') {
    busy = value;
    input.disabled = value;
    sendButton.disabled = value;
    if (message) status.textContent = message;
  }

  async function cartContext() {
    try {
      const response = await fetch('/cart.js', { headers: { Accept: 'application/json' }, cache: 'no-store' });
      if (!response.ok) return 0;
      const cart = await response.json();
      return Math.max(0, Number(cart.item_count) || 0);
    } catch (_) { return 0; }
  }

  function normalizeProduct(product) {
    if (!product || typeof product !== 'object') return null;
    const url = String(product.url || (product.handle ? `/products/${product.handle}` : ''));
    if (!url.startsWith('/')) return null;
    return {
      available: product.available !== false,
      handle: clean(product.handle || url.split('/products/')[1]?.split(/[?#/]/)[0], 80),
      price: clean(product.price || product.price_min || '', 32),
      productType: clean(product.type || product.product_type || 'Product', 64),
      title: clean(product.title, 120),
      url: url.slice(0, 180),
      variantId: /^\d{1,24}$/.test(String(product.variantId || '')) ? String(product.variantId) : undefined
    };
  }

  async function catalogSearch(query) {
    const text = clean(query, 100);
    if (!text) return [];
    try {
      const params = new URLSearchParams({
        q: text,
        'resources[type]': 'product',
        'resources[limit]': '6',
        'resources[options][unavailable_products]': 'hide'
      });
      const response = await fetch(`/search/suggest.json?${params}`, { headers: { Accept: 'application/json' }, cache: 'no-store' });
      if (!response.ok) return [];
      const body = await response.json();
      const products = body?.resources?.results?.products;
      return Array.isArray(products) ? products.map(normalizeProduct).filter(Boolean).slice(0, 6) : [];
    } catch (_) { return []; }
  }

  function shouldSearch(message) {
    return /\b(find|choose|compare|recommend|best|option|product|accessor|fit|compatible|looking|need|want)\b/i.test(message);
  }

  async function renderProducts(root, query) {
    const products = await catalogSearch(query);
    if (!products.length) return;
    const list = d.createElement('div');
    list.className = 'kra-products';
    products.slice(0, 4).forEach((product) => {
      const link = d.createElement('a');
      link.className = 'kra-product';
      link.href = product.url;
      const title = d.createElement('span');
      title.textContent = product.title;
      const price = d.createElement('small');
      price.textContent = product.price || 'View';
      link.append(title, price);
      link.addEventListener('click', () => emit('relationship_product_open', { handle: product.handle }));
      list.appendChild(link);
    });
    root.appendChild(list);
    conversation.scrollTop = conversation.scrollHeight;
  }

  async function addVariant(variantId, button) {
    if (!/^\d{1,24}$/.test(String(variantId))) return;
    button.disabled = true;
    const old = button.textContent;
    button.textContent = 'Adding…';
    try {
      const response = await fetch(w.routes?.cart_add_url || '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: Number(variantId), quantity: 1 })
      });
      if (!response.ok) throw new Error('cart_add_failed');
      button.textContent = 'Added ✓';
      appendMessage('assistant', 'Done — I added the confirmed item to your cart. I can also help you double-check compatibility before checkout.');
      emit('relationship_action_complete', { action: 'add_to_cart' });
    } catch (_) {
      button.textContent = 'Could not add';
      appendMessage('assistant', 'I couldn’t safely add that item. Open the product page to confirm its current variant and availability.');
    } finally {
      w.setTimeout(() => { button.disabled = false; if (button.textContent === 'Could not add') button.textContent = old; }, 1800);
    }
  }

  function renderActions(root, actions, suggestions) {
    const row = d.createElement('div');
    row.className = 'kra-action-row';
    (Array.isArray(actions) ? actions : []).slice(0, 3).forEach((action) => {
      if (!action || !action.type) return;
      if (action.type === 'open_path' && String(action.path || '').startsWith('/')) {
        const link = d.createElement('a');
        link.href = action.path;
        link.textContent = clean(action.label, 48) || 'Open';
        row.appendChild(link);
      } else {
        const button = d.createElement('button');
        button.type = 'button';
        button.textContent = `${action.requiresConfirmation ? 'Confirm: ' : ''}${clean(action.label, 48)}`;
        if (action.type === 'add_to_cart') button.addEventListener('click', () => addVariant(action.variantId, button));
        if (action.type === 'search_catalog') button.addEventListener('click', () => renderProducts(root, action.query));
        if (action.type === 'show_memory') button.addEventListener('click', openMemory);
        if (action.type === 'show_age_gate') button.addEventListener('click', () => {
          const gate = $('#KloudzAgeGate, [data-kloudz-age-gate], .kloudz-age-gate');
          if (gate) gate.scrollIntoView({ behavior: 'smooth', block: 'center' });
          appendMessage('assistant', 'The store’s age verification and your local law remain required. I won’t bypass either.');
        });
        row.appendChild(button);
      }
      if (action.type === 'search_catalog' && action.query) renderProducts(root, action.query);
    });
    (Array.isArray(suggestions) ? suggestions : []).slice(0, 3).forEach((suggestion) => {
      const button = d.createElement('button');
      button.type = 'button';
      button.textContent = clean(suggestion, 80);
      button.addEventListener('click', () => { input.value = suggestion; sendCurrentMessage(); });
      row.appendChild(button);
    });
    if (row.childNodes.length) root.appendChild(row);
  }

  function pushHistory(role, content) {
    transientHistory.push({ role, content: clean(content, 1000), at: Date.now() });
    if (transientHistory.length > 12) transientHistory.splice(0, transientHistory.length - 12);
  }

  async function requestChat(payload, retried = false) {
    try {
      return await callAPI('/v1/assistant/chat', payload);
    } catch (error) {
      if (!retried && error && error.status === 401) {
        payload.sessionToken = await ensureSession(true);
        return requestChat(payload, true);
      }
      throw error;
    }
  }

  async function sendCurrentMessage() {
    if (busy) return;
    const message = clean(input.value, 1200);
    if (!message) return;
    input.value = '';
    const userDisplay = selectedImage ? `${message}\n[Image attached for visual guidance]` : message;
    appendMessage('user', userDisplay);
    pushHistory('user', message);
    const typing = appendTyping();
    setBusy(true, selectedImage ? 'Kloudz is reviewing your image and context…' : 'Kloudz is consulting the right specialists…');
    try {
      const [sessionToken, cartItemCount, catalog] = await Promise.all([
        ensureSession(),
        cartContext(),
        shouldSearch(message) ? catalogSearch(message) : Promise.resolve([])
      ]);
      const payload = {
        protocol: 'kloudz-relationship-chat/1',
        sessionToken,
        message,
        imageDataUrl: selectedImage?.dataUrl,
        consent: { analytics: privacy.analytics, memory: privacy.memory },
        context: {
          pagePath: location.pathname.slice(0, 160),
          pageTitle: clean(d.title, 160),
          pageType: pageContext.pageType,
          currency: pageContext.currency,
          locale: pageContext.locale,
          cartItemCount,
          product: normalizeProduct(pageContext.product)
        },
        catalog,
        transientHistory: transientHistory.slice(-12)
      };
      const result = await requestChat(payload);
      typing.remove();
      const metaParts = [];
      if (result.emotion) metaParts.push(`Mood: ${clean(result.emotion, 24)}`);
      if (Array.isArray(result.specialists)) metaParts.push(result.specialists.slice(0, 3).map((name) => clean(name, 32).replace(/_/g, ' ')).join(' · '));
      const article = appendMessage('assistant', clean(result.reply, 1600), {
        meta: metaParts.join(' · '), why: clean(result.explainability, 280)
      });
      renderActions(article, result.actions, result.suggestions);
      pushHistory('assistant', result.reply);
      selectedImage = null;
      imageInput.value = '';
      attachment.hidden = true;
      status.textContent = result.source === 'safe_fallback'
        ? 'Safe fallback used. You can keep asking; legal, privacy, and age safeguards remain active.'
        : `Guidance checked for helpfulness, warmth, accuracy, and relationship impact${privacy.memory ? ' · memory on' : ' · memory off'}.`;
      emit('relationship_assistant_response', {
        source: result.source, emotion: result.emotion, actionCount: Array.isArray(result.actions) ? result.actions.length : 0
      });
    } catch (error) {
      typing.remove();
      appendMessage('assistant', 'I’m still here, but my secure connection paused. Please try again in a moment; I won’t guess or take an unsafe action while disconnected.');
      status.textContent = 'Connection paused · no action was taken.';
    } finally {
      setBusy(false);
      input.focus();
    }
  }

  async function memoryRequest(action, items) {
    const sessionToken = await ensureSession();
    return callAPI('/v1/assistant/memory', {
      protocol: 'kloudz-relationship-memory/1', sessionToken, action,
      consent: { memory: privacy.memory, analytics: privacy.analytics }, items
    }, 12000);
  }

  function fillMemory(memory) {
    const values = {};
    (Array.isArray(memory?.items) ? memory.items : []).forEach((item) => { values[item.key] = item.value; });
    $$('input[name]', memoryForm).forEach((field) => { field.value = values[field.name] || ''; });
    const summary = $('[data-kra-memory-summary]', memoryPanel);
    if (summary) summary.textContent = memory?.summary || 'No relationship memory saved yet.';
  }

  async function openMemory() {
    memoryPanel.hidden = false;
    await refreshPrivacy();
    try {
      const result = await memoryRequest('inspect');
      fillMemory(result.memory);
    } catch (_) {
      fillMemory({ summary: privacy.memory ? 'Memory could not be loaded yet.' : 'Memory is off until preference consent is granted.' });
    }
  }

  async function resizeImage(file) {
    if (!file || !/^image\/(jpeg|png|webp)$/.test(file.type) || file.size > 8_000_000) throw new Error('invalid_image');
    const source = await createImageBitmap(file);
    const scale = Math.min(1, 640 / Math.max(source.width, source.height));
    const canvas = d.createElement('canvas');
    canvas.width = Math.max(1, Math.round(source.width * scale));
    canvas.height = Math.max(1, Math.round(source.height * scale));
    canvas.getContext('2d', { alpha: false }).drawImage(source, 0, 0, canvas.width, canvas.height);
    source.close();
    let quality = .78;
    let dataUrl = canvas.toDataURL('image/jpeg', quality);
    while (dataUrl.length > 390000 && quality > .42) {
      quality -= .08;
      dataUrl = canvas.toDataURL('image/jpeg', quality);
    }
    if (dataUrl.length > 420000) throw new Error('image_too_large');
    return { dataUrl, name: clean(file.name, 80) || 'Customer image' };
  }

  d.documentElement.dataset.kraStage = 'binding';
  launcher.addEventListener('pointerdown', (event) => {
    if (event.button !== 0) return;
    lastLauncherPointer = Date.now();
    event.preventDefault();
    event.stopImmediatePropagation();
    panel.hidden ? openPanel() : closePanel();
  });
  launcher.addEventListener('click', (event) => {
    event.preventDefault();
    event.stopImmediatePropagation();
    if (Date.now() - lastLauncherPointer < 700) return;
    panel.hidden ? openPanel() : closePanel();
  });
  $('[data-kra-close]', panel).addEventListener('click', closePanel);
  $('[data-kra-memory-open]', panel).addEventListener('click', openMemory);
  $('[data-kra-memory-close]', panel).addEventListener('click', () => { memoryPanel.hidden = true; input.focus(); });
  $('[data-kra-attachment-remove]', panel).addEventListener('click', () => {
    selectedImage = null; imageInput.value = ''; attachment.hidden = true;
  });
  $$('[data-kra-starters] button', panel).forEach((button) => button.addEventListener('click', () => {
    const prompt = button.textContent || '';
    if (/remember/i.test(prompt)) openMemory();
    else { input.value = prompt; sendCurrentMessage(); }
  }));
  form.addEventListener('submit', (event) => { event.preventDefault(); sendCurrentMessage(); });
  input.addEventListener('keydown', (event) => {
    if (event.key === 'Enter' && !event.shiftKey) { event.preventDefault(); sendCurrentMessage(); }
  });
  input.addEventListener('input', () => {
    input.style.height = 'auto';
    input.style.height = `${Math.min(110, input.scrollHeight)}px`;
  });
  imageInput.addEventListener('change', async () => {
    const file = imageInput.files && imageInput.files[0];
    if (!file) return;
    status.textContent = 'Preparing the image privately for visual guidance…';
    try {
      selectedImage = await resizeImage(file);
      attachmentPreview.src = selectedImage.dataUrl;
      attachmentName.textContent = selectedImage.name;
      attachment.hidden = false;
      status.textContent = 'Image ready. It is sent only when you send your message.';
    } catch (_) {
      selectedImage = null; imageInput.value = '';
      status.textContent = 'Use a JPEG, PNG, or WebP image under 8 MB.';
    }
  });
  memoryForm.addEventListener('submit', async (event) => {
    event.preventDefault();
    if (!privacy.memory) return;
    const items = $$('input[name]', memoryForm).map((field) => ({ key: field.name, value: clean(field.value, 120) }));
    try {
      const result = await memoryRequest('set', items);
      fillMemory(result.memory);
      $('[data-kra-memory-summary]', memoryPanel).textContent = 'Saved. You can change or erase this at any time.';
      emit('relationship_memory_update', { itemCount: result.memory?.items?.length || 0 });
    } catch (_) { $('[data-kra-memory-summary]', memoryPanel).textContent = 'Memory could not be saved yet.'; }
  });
  $('[data-kra-memory-clear]', memoryPanel).addEventListener('click', async () => {
    if (!w.confirm('Erase all Kloudz relationship memory for this assistant session?')) return;
    try {
      const result = await memoryRequest('clear');
      fillMemory(result.memory);
      removeToken();
      $('[data-kra-memory-summary]', memoryPanel).textContent = 'Everything was erased.';
      emit('relationship_memory_clear');
    } catch (_) { $('[data-kra-memory-summary]', memoryPanel).textContent = 'Memory could not be erased yet.'; }
  });
  d.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && !memoryPanel.hidden) { memoryPanel.hidden = true; input.focus(); }
    else if (event.key === 'Escape' && !panel.hidden) closePanel();
  });

  const SpeechRecognition = w.SpeechRecognition || w.webkitSpeechRecognition;
  const voiceButton = $('[data-kra-voice]', panel);
  if (SpeechRecognition && voiceButton) {
    voiceButton.hidden = false;
    voiceButton.addEventListener('click', () => {
      const recognition = new SpeechRecognition();
      recognition.lang = pageContext.locale || 'en-US';
      recognition.interimResults = false;
      recognition.maxAlternatives = 1;
      recognition.onstart = () => { voiceButton.disabled = true; status.textContent = 'Listening…'; };
      recognition.onresult = (event) => {
        input.value = clean(event.results?.[0]?.[0]?.transcript, 1200);
        status.textContent = 'Voice captured. Review it, then send.';
      };
      recognition.onerror = () => { status.textContent = 'Voice input was not available. You can keep typing.'; };
      recognition.onend = () => { voiceButton.disabled = false; };
      recognition.start();
    });
  }

  d.addEventListener('visitorConsentCollected', refreshPrivacy);
  w.addEventListener('visitorConsentCollected', refreshPrivacy);
  w.setTimeout(() => {
    if (panel.hidden) {
      unread.hidden = false;
      launcher.setAttribute('aria-label', 'Kloudz has a context-aware offer to help');
    }
  }, pageContext.product ? 9000 : 18000);

  refreshPrivacy();
  w.KloudzRelationshipAssistant = Object.freeze({
    version: VERSION,
    open: openPanel,
    close: closePanel,
    ask: (message) => { openPanel(); input.value = clean(message, 1200); sendCurrentMessage(); },
    memory: openMemory
  });
  d.documentElement.dataset.kraStage = 'ready';
})();
